import TwilioVoiceClient from './twilioVoiceClient';
import SipVoiceClient from './sipVoiceClient';
import VoiceAPI from './voiceAPIClient';
import { useCallsStore } from 'dashboard/stores/calls';
import { getActivePinia } from 'pinia';

// GUARD U1 wiring: useCallSession gọi VoiceAPI.joinConference TRỰC TIẾP (không
// qua proxy) và overlay không được sửa useCallSession ngoài dòng import — nên
// chặn tại singleton VoiceAPI. Response POST conference có bridge_id (inbound
// asterisk) → arm auto-accept cho sipVoiceClient NGAY khi response về, trước
// khi useCallSession xử lý tiếp. Shape response trả về caller giữ nguyên.
const originalJoinConference = VoiceAPI.joinConference.bind(VoiceAPI);
VoiceAPI.joinConference = args =>
  originalJoinConference(args).then(response => {
    try {
      if (response?.bridge_id) SipVoiceClient.armAutoAccept(response.bridge_id);
    } catch (e) {
      // Lỗi arming không được làm hỏng flow Join
      // eslint-disable-next-line no-console
      console.error('[voice] armAutoAccept failed:', e);
    }
    return response;
  });

// Provider mặc định khi không đọc được store. Đặt 'asterisk' vì môi trường này
// dùng Asterisk; tránh rơi nhầm về Twilio (gây "Invalid token" với SIP creds).
const DEFAULT_PROVIDER =
  (typeof window !== 'undefined' && window.__VOICE_DEFAULT_PROVIDER__) ||
  'asterisk';

// Proxy giữ NGUYÊN API surface của TwilioVoiceClient để useCallSession.js chỉ
// cần đổi đúng 1 dòng import (twilioVoiceClient → voiceClientProxy), 8 chỗ gọi
// còn lại không đổi.
//
// Mỗi lời gọi được route tới đúng client theo provider của cuộc gọi đang hoạt
// động: 'asterisk' → SipVoiceClient, còn lại → TwilioVoiceClient (mặc định).
//
// EventTarget: proxy tự là EventTarget và forward event 'call:disconnected' từ
// CẢ hai client, nên addEventListener/removeEventListener của useCallSession
// vẫn bắt được sự kiện dù cuộc gọi là twilio hay asterisk.

const clientForProvider = provider =>
  provider === 'asterisk' ? SipVoiceClient : TwilioVoiceClient;

// Xác định client hiện hành từ store.
// Ưu tiên: active call → call asterisk bất kỳ (đang ringing) → call mới nhất.
// Lý do: lúc initializeDevice() call chưa active; nếu có call asterisk đang chờ
// thì phải dùng SIP client (không rơi về Twilio do calls[0] là call cũ).
const resolveActiveClient = () => {
  try {
    // getActivePinia() hoạt động NGOÀI Vue component (module scope), khác với
    // useCallsStore() cần active setup context. Truyền pinia instance để lấy store.
    const pinia = getActivePinia();
    const store = pinia ? useCallsStore(pinia) : useCallsStore();
    const calls = store.calls || [];
    // Nếu có BẤT KỲ call asterisk nào → dùng SIP client (kể cả chưa active).
    const hasAsterisk = calls.some(c => c?.provider === 'asterisk');
    if (hasAsterisk) return SipVoiceClient;
    const call = store.activeCall || calls[0];
    return clientForProvider(call?.provider || DEFAULT_PROVIDER);
  } catch {
    // Không đọc được store → dùng provider mặc định (asterisk), KHÔNG rơi về Twilio.
    return clientForProvider(DEFAULT_PROVIDER);
  }
};

class VoiceClientProxy extends EventTarget {
  constructor() {
    super();
    // Forward event 'call:disconnected' từ cả hai client ra proxy.
    const forwardDisconnect = () =>
      this.dispatchEvent(new CustomEvent('call:disconnected'));
    TwilioVoiceClient.addEventListener('call:disconnected', forwardDisconnect);
    SipVoiceClient.addEventListener('call:disconnected', forwardDisconnect);
    // Forward event 'call:incoming' (INBOUND) từ SIP client ra proxy để
    // useCallSession bắt được và hiện pop-up khi Asterisk INVITE thẳng tới agent.
    SipVoiceClient.addEventListener('call:incoming', e => {
      this.dispatchEvent(new CustomEvent('call:incoming', { detail: e.detail }));
    });
  }

  // Đăng ký SIP nền (INBOUND): agent online thường trực để nhận cuộc gọi đến.
  // Chỉ SIP client cần; Twilio không có khái niệm này (no-op an toàn).
  registerBackground(inboxId) {
    if (typeof SipVoiceClient.registerBackground === 'function') {
      return SipVoiceClient.registerBackground(inboxId);
    }
    return null;
  }

  // Chấp nhận cuộc gọi đến (agent bấm nghe) — route tới client đang có incoming.
  acceptIncoming() {
    if (typeof SipVoiceClient.acceptIncoming === 'function') {
      return SipVoiceClient.acceptIncoming();
    }
    return null;
  }

  get hasActiveConnection() {
    return (
      TwilioVoiceClient.hasActiveConnection ||
      SipVoiceClient.hasActiveConnection
    );
  }

  // initializeDevice nhận inboxId — client được chọn theo active call provider.
  initializeDevice(inboxId) {
    return resolveActiveClient().initializeDevice(inboxId);
  }

  joinClientCall(args) {
    return resolveActiveClient().joinClientCall(args);
  }

  setMuted(shouldMute) {
    return resolveActiveClient().setMuted(shouldMute);
  }

  // end/destroy gọi trên CẢ hai client cho an toàn (tránh sót session khi
  // provider vừa đổi hoặc store đã bị dọn trước khi kết thúc).
  endClientCall() {
    TwilioVoiceClient.endClientCall();
    SipVoiceClient.endClientCall();
  }

  destroyDevice() {
    TwilioVoiceClient.destroyDevice();
    SipVoiceClient.destroyDevice();
  }
}

export default new VoiceClientProxy();
